<script setup lang="ts">
import { computed, ref } from "vue";
import {
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Diamond,
  ListChecks,
} from "lucide-vue-next";
import type { ProjectTask } from "@/types/models";
import { todayLocalDate } from "@/utils/dates";

const props = defineProps<{ tasks: ProjectTask[] }>();
const emit = defineEmits<{ openTask: [id: string] }>();
const todayIso = todayLocalDate();
const today = new Date(`${todayIso}T12:00:00`);
const month = ref(new Date(today.getFullYear(), today.getMonth(), 1, 12));
const scale = ref<"month" | "week">("month");
const expandedDays = ref(new Set<string>());
const heading = computed(() => {
  if (scale.value === "month")
    return new Intl.DateTimeFormat("en-CA", {
      month: "long",
      year: "numeric",
    }).format(month.value);
  const start = new Date(month.value);
  start.setDate(start.getDate() - start.getDay());
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return `${new Intl.DateTimeFormat("en-CA", { month: "short", day: "numeric" }).format(start)}–${new Intl.DateTimeFormat("en-CA", { month: "short", day: "numeric", year: "numeric" }).format(end)}`;
});
const days = computed(() =>
  Array.from({ length: scale.value === "month" ? 42 : 7 }, (_, index) => {
    const first =
      scale.value === "month"
        ? new Date(month.value.getFullYear(), month.value.getMonth(), 1, 12)
        : new Date(month.value);
    const date = new Date(
      first.getFullYear(),
      first.getMonth(),
      first.getDate() - first.getDay() + index,
      12,
    );
    const iso = date.toISOString().slice(0, 10);
    return {
      iso,
      day: date.getDate(),
      current:
        scale.value === "week" || date.getMonth() === month.value.getMonth(),
      today: iso === todayIso,
      tasks: props.tasks.filter(
        (task) =>
          task.dueDate === iso ||
          (task.type === "milestone" && task.startDate === iso),
      ),
    };
  }),
);

function shiftMonth(amount: number) {
  month.value =
    scale.value === "month"
      ? new Date(
          month.value.getFullYear(),
          month.value.getMonth() + amount,
          1,
          12,
        )
      : new Date(
          month.value.getFullYear(),
          month.value.getMonth(),
          month.value.getDate() + amount * 7,
          12,
        );
  expandedDays.value = new Set();
}
function goToday() {
  const value = new Date(`${todayIso}T12:00:00`);
  month.value =
    scale.value === "month"
      ? new Date(value.getFullYear(), value.getMonth(), 1, 12)
      : value;
}
function setScale(nextScale: "month" | "week") {
  if (scale.value === nextScale) return;
  scale.value = nextScale;
  expandedDays.value = new Set();
}
function toggleDay(iso: string) {
  const next = new Set(expandedDays.value);
  next.has(iso) ? next.delete(iso) : next.add(iso);
  expandedDays.value = next;
}
function isDayExpanded(iso: string) {
  return scale.value === "week"
    ? !expandedDays.value.has(iso)
    : expandedDays.value.has(iso);
}
</script>

<template>
  <div class="calendar-board">
    <header>
      <div>
        <button
          class="icon-button"
          type="button"
          :aria-label="`Previous ${scale}`"
          @click="shiftMonth(-1)"
        >
          <ChevronLeft :size="18" />
        </button>
        <h2>{{ heading }}</h2>
        <button
          class="icon-button"
          type="button"
          :aria-label="`Next ${scale}`"
          @click="shiftMonth(1)"
        >
          <ChevronRight :size="18" /></button
        ><button
          class="button button--secondary"
          type="button"
          @click="goToday"
        >
          Today
        </button>
      </div>
      <div class="segmented" role="group" aria-label="Calendar scale">
        <button
          type="button"
          :class="{ active: scale === 'month' }"
          :aria-pressed="scale === 'month'"
          @click="setScale('month')"
        >
          Month</button
        ><button
          type="button"
          :class="{ active: scale === 'week' }"
          :aria-pressed="scale === 'week'"
          @click="setScale('week')"
        >
          Week
        </button>
      </div>
    </header>
    <div class="calendar-scroll">
      <div
        class="calendar-grid"
        :class="{ 'calendar-grid--week': scale === 'week' }"
      >
        <span
          v-for="weekday in [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
          ]"
          :key="weekday"
          class="weekday"
          >{{ weekday }}</span
        >
        <div
          v-for="day in days"
          :key="day.iso"
          class="calendar-day"
          :class="{ muted: !day.current, today: day.today }"
        >
          <div class="calendar-day__header">
            <time>{{ day.day }}<small v-if="day.today">Today</small></time>
            <button
              v-if="day.tasks.length > 3"
              class="more-button"
              type="button"
              :aria-expanded="isDayExpanded(day.iso)"
              :aria-label="`${isDayExpanded(day.iso) ? 'Show fewer tasks' : `Show ${day.tasks.length - 3} more tasks`} for ${day.iso}`"
              @click="toggleDay(day.iso)"
            >
              {{
                isDayExpanded(day.iso)
                  ? "Show fewer"
                  : `${day.tasks.length - 3} more`
              }}
            </button>
          </div>
          <button
            v-for="task in day.tasks.slice(
              0,
              isDayExpanded(day.iso) ? day.tasks.length : 3,
            )"
            :key="task.id"
            type="button"
            :class="[
              `calendar-task calendar-task--${task.type}`,
              task.visibility === 'Internal' ? 'calendar-task--internal' : '',
            ]"
            @click="emit('openTask', task.id)"
          >
            <Diamond
              v-if="task.type === 'milestone'"
              :size="9"
              fill="currentColor"
            /><ListChecks
              v-else-if="task.type === 'checklist'"
              :size="10"
            /><ClipboardList
              v-else-if="task.type === 'questionnaire'"
              :size="10"
            /><span>{{ task.title }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.calendar-board {
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
  background: white;
}
.calendar-board > header {
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  border-bottom: 1px solid var(--rule);
}
.calendar-board > header > div:first-child {
  display: flex;
  align-items: center;
  gap: 5px;
}
.calendar-board h2 {
  margin: 0 8px;
  font-size: 17px;
}
.segmented {
  display: flex;
  padding: 2px;
  border: 1px solid var(--rule);
  border-radius: 5px;
  background: var(--sheet-strong);
}
.segmented button,
.segmented span {
  min-height: 29px;
  padding: 0 10px;
  border-radius: 3px;
  background: transparent;
  color: var(--text-soft);
  font-size: 12px;
}
.segmented .active {
  background: white;
  color: var(--text);
  box-shadow: 0 1px 3px rgba(8, 27, 46, 0.1);
}
.calendar-scroll {
  min-height: 0;
  overflow: auto;
}
.calendar-grid {
  min-width: 900px;
  min-height: 100%;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: 34px repeat(6, minmax(105px, auto));
}
.calendar-grid--week {
  grid-template-rows: 34px minmax(260px, auto);
}
.weekday {
  display: flex;
  align-items: center;
  padding: 0 8px;
  border-right: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
  background: var(--sheet-subtle);
  color: var(--text-faint);
  font-size: 10px;
  font-weight: 750;
  text-transform: uppercase;
}
.calendar-day {
  min-width: 0;
  padding: 6px;
  border-right: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
}
.calendar-day__header {
  min-height: 17px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin-bottom: 4px;
}
.calendar-day__header time {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--text-soft);
  font-size: 11px;
}
.calendar-day__header time small {
  padding: 1px 4px;
  border-radius: 3px;
  background: var(--shared);
  color: white;
  font-size: 8px;
}
.calendar-day.today {
  background: #f8fcfa;
  box-shadow: inset 2px 0 var(--brand);
}
.calendar-task {
  width: 100%;
  min-height: 24px;
  margin-bottom: 3px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 5px;
  border-radius: 3px;
  overflow: hidden;
  background: var(--shared-soft);
  color: #164f82;
  font-size: 10px;
  text-align: left;
  cursor: pointer;
}
.calendar-task span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.calendar-task--milestone {
  color: #71500f;
  background: var(--warning-soft);
}
.calendar-task--internal {
  color: #59426c;
  background: var(--internal-soft);
}
.more-button {
  min-height: 20px;
  padding: 1px 3px;
  background: transparent;
  color: var(--shared);
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
}
@media (pointer: coarse) {
  .calendar-task,
  .more-button {
    position: relative;
  }
  .calendar-task::after,
  .more-button::after {
    content: "";
    position: absolute;
    inset: -10px -4px;
  }
}
@media (max-width: 700px) {
  .calendar-board > header {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
  }
  .calendar-board > header > div:first-child {
    justify-content: space-between;
  }
  .calendar-grid {
    min-width: 760px;
  }
  .weekday {
    font-size: 9px;
  }
}
</style>
